import { useEffect, useState } from "react";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { startOfToday } from "date-fns";
import { z } from "zod";
import { machineFormat } from "@medoma/models/date-and-time";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { Text } from "@medoma/components/Text/Text";
import ClockIcon from "@medoma/components/icons/ClockIcon";
import HiddenIcon from "@medoma/components/icons/HiddenIcon";
import PenIcon from "@medoma/components/icons/PenIcon";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import RecurrenceIcon from "@medoma/components/icons/RecurrenceIcon";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import {
  addActivitiesFromGroupTemplate,
  type INewActivity,
} from "@/api/Activities";
import { activityRequirementSchema } from "@medoma/models/activityRequirements";
import {
  batchTemplateActivityDelayedStartDateOptionsSchema,
  batchTemplateActivityDelayedStartDateUnitSchema,
  batchTemplateActivityInclusionTypeSchema,
  batchTemplateActivityTimeConfigTypeSchema,
  type IBatchTemplate,
  type IBatchTemplateActivity,
} from "@/api/Templates";
import { deducedError } from "@/Utils/ErrorUtils";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import MeasurementChip from "@/components/Chips/MeasurementChip";
import NoResults from "@/components/NoResults/NoResults";
import { RequirementChip } from "@/components/Chips/RequirementChip";
import Checkbox from "@/components/Checkbox/Checkbox";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import Form from "@/components/Form/Form";
import { FormFieldPathPrefixContextProvider } from "@/forms/FormFieldPathPrefixContextProvider";
import { TemplateFieldValidationContextProvider } from "@/forms/AddActivity/TemplateFieldValidationContextProvider";
import { TemplateDateInput } from "@/forms/AddActivity/TemplateDateInput";
import { TemplateDescriptionInput } from "@/forms/AddActivity/TemplateDescriptionInput";
import { TemplateScheduling } from "@/forms/AddActivity/Scheduling/TemplateScheduling";
import { TemplateTimeSlotsPicker } from "@/forms/AddActivity/Scheduling/TemplateTimeSlotsPicker";
import {
  activityFrequencySchema,
  weekdayDictionary,
} from "@/forms/AddActivity/Scheduling/schedulingModels";
import { DateInput } from "@/forms/AddActivity/Scheduling/DateInput";
import { newActivityFromBatchTemplateActivity } from "./newActivityFromBatchTemplateActivity";
import { resolveActivityStartDate } from "./resolveActivityStartDate";
import { getRecurrenceLabel } from "@/forms/AddActivity/templateUtils";
import type { CreateActivitiesFromBatchTemplateFormFields } from "./createActivitiesFromBatchTemplateFormSchema";
import styles from "./CreateActivitiesFromBatchTemplateForm.module.scss";

export const CreateActivitiesFromBatchTemplateForm = ({
  template,
  onSuccess,
}: {
  template: IBatchTemplate;
  onSuccess: () => void;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());

  const activities = template.template.activities;

  const activityDefaults = Object.fromEntries(
    activities.map((activity) => {
      const isDefaultSelected =
        activity.inclusionType ===
        batchTemplateActivityInclusionTypeSchema.enum
          .optionalWithDefaultSelected;

      return [
        activity.id,
        {
          selected: isDefaultSelected,
          description: activity.description,
          timeCategory: activity.timeCategory,
          recurrencesPerDay: activity.recurrencesPerDay,
          timeslots: activity.timeslots,
          timeSensitivity: activity.timeSensitivity,
        },
      ];
    }),
  );

  const methods = useForm<CreateActivitiesFromBatchTemplateFormFields>({
    defaultValues: {
      startDate: machineFormat(startOfToday(), "yyyy-MM-dd"),
      selectedActivities: activityDefaults,
      sharedTimeConfig: template.template.sharedTimeConfig,
    },
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
    setError,
    reset,
    getValues,
    watch,
  } = methods;

  // oxlint-disable-next-line react/incompatible-library
  const sharedFrequency = watch("sharedTimeConfig.frequency");
  const hasSharedTimeConfigActivities =
    activities.filter(
      (a) =>
        a.timeConfigType ===
        batchTemplateActivityTimeConfigTypeSchema.enum.shared,
    ).length > 0;
  const hasRecurringSharedTimeConfig =
    hasSharedTimeConfigActivities &&
    sharedFrequency === activityFrequencySchema.enum.recurring;

  const showBatchEndDateInput = hasRecurringSharedTimeConfig;

  const { mutate, isPending } = useMutation({
    mutationFn: async (
      formData: CreateActivitiesFromBatchTemplateFormFields,
    ) => {
      const selectedActivities = activities.filter(
        (activity) => formData.selectedActivities[activity.id]?.selected,
      );

      const batchStartDate = formData.startDate;
      const batchEndDate = formData.endDate;

      const newActivities: INewActivity[] = selectedActivities.map(
        (activity) => {
          const selectedActivityFormData =
            formData.selectedActivities[activity.id];

          const usesBatchConfig =
            activity.timeConfigType ===
            batchTemplateActivityTimeConfigTypeSchema.enum.shared;

          const activityHasOwnStartDate = selectedActivityFormData?.startDate;

          const startDate = usesBatchConfig
            ? batchStartDate
            : activityHasOwnStartDate
              ? (selectedActivityFormData.startDate as string)
              : resolveActivityStartDate(activity, batchStartDate);

          const endDate = usesBatchConfig
            ? batchEndDate
            : selectedActivityFormData?.endDate;

          const activitySchedule = usesBatchConfig
            ? formData.sharedTimeConfig
            : {
                timeCategory: selectedActivityFormData?.timeCategory,
                recurrencesPerDay: selectedActivityFormData?.recurrencesPerDay,
                timeslots: selectedActivityFormData?.timeslots,
                timeSensitivity: selectedActivityFormData?.timeSensitivity,
              };

          return newActivityFromBatchTemplateActivity({
            activity: {
              ...activity,
              ...activitySchedule,
              description: selectedActivityFormData?.description,
            },
            startDate,
            endDate,
            patientId,
            templateId: template.templateId,
            templateRevision: template.revisionNumber,
          });
        },
      );

      await addActivitiesFromGroupTemplate({
        templateId: template.templateId,
        templateRevision: template.revisionNumber,
        newActivities,
      });
    },
    onError: (error) => {
      setError("root.server", { message: deducedError(error) });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const anySelected = activities.some(
      (a) => formData.selectedActivities[a.id]?.selected,
    );
    if (!anySelected) {
      setError("root.noSelectedActivities", {
        message: t`Välj minst en aktivitet`,
      });
      return;
    }
    mutate(formData);
  });

  if (activities.length === 0) {
    return (
      <NoResults
        message={t`Inga aktiviteter hittades. Kontrollera att du har valt rätt mall och att mallen innehåller aktiviteter.`}
      />
    );
  }

  const activitiesWithSharedTimeConfig = activities.filter(
    (activity) =>
      activity.timeConfigType ===
      batchTemplateActivityTimeConfigTypeSchema.enum.shared,
  );
  const activitiesWithIndividualTimeConfig = activities.filter(
    (activity) =>
      activity.timeConfigType ===
      batchTemplateActivityTimeConfigTypeSchema.enum.individual,
  );

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {activitiesWithSharedTimeConfig.length > 0 ? (
          <section className={styles.activitiesWithSharedTimeConfig}>
            <Checkboxes width="fill-container">
              {activitiesWithSharedTimeConfig.map((activity) => (
                <ActivityRow key={activity.id} activity={activity} />
              ))}
            </Checkboxes>
            <FormFieldPathPrefixContextProvider prefix="sharedTimeConfig">
              <TemplateFieldValidationContextProvider
                value={{ isInFinalValidationPhase: true }}
              >
                <div className={styles.sharedTimeConfigFields}>
                  <TemplateScheduling />
                </div>
              </TemplateFieldValidationContextProvider>
            </FormFieldPathPrefixContextProvider>
          </section>
        ) : null}

        <DateInput hasEndDate={showBatchEndDateInput} />

        {activitiesWithSharedTimeConfig.length > 0 &&
        activitiesWithIndividualTimeConfig.length > 0 ? (
          <div className={styles.divider} />
        ) : null}

        {activitiesWithIndividualTimeConfig.length > 0 ? (
          <section>
            <Checkboxes width="fill-container">
              {activitiesWithIndividualTimeConfig.map((activity) => (
                <ActivityRow key={activity.id} activity={activity} />
              ))}
            </Checkboxes>
          </section>
        ) : null}

        {errors.root?.noSelectedActivities?.message ? (
          <ErrorMessage
            message={errors.root.noSelectedActivities.message}
            weight="bold"
          />
        ) : null}

        {errors.root?.server?.message && !isDirty ? (
          <ErrorMessage message={errors.root.server.message} weight="bold" />
        ) : null}

        <div className={styles.submit}>
          <FilledButton type="submit" disabled={isPending}>
            <Trans>Lägg till aktiviteter</Trans>
          </FilledButton>
        </div>
      </Form>
    </FormProvider>
  );
};

const ActivityRow = ({ activity }: { activity: IBatchTemplateActivity }) => {
  const { t } = useLingui();
  const { clearErrors, register, watch, resetField } =
    useFormContext<CreateActivitiesFromBatchTemplateFormFields>();

  const [showDescriptionInput, setShowDescriptionInput] = useState(false);
  const [showTimeInput, setShowTimeInput] = useState(false);
  const [showDateInput, setShowDateInput] = useState(false);

  const activityIsSelected = watch(
    `selectedActivities.${activity.id}.selected`,
  );

  useEffect(() => {
    if (!activityIsSelected) {
      resetField(`selectedActivities.${activity.id}.description`);
      resetField(`selectedActivities.${activity.id}.startDate`);
      resetField(`selectedActivities.${activity.id}.endDate`);
      resetField(`selectedActivities.${activity.id}.timeCategory`);
      resetField(`selectedActivities.${activity.id}.recurrencesPerDay`);
      resetField(`selectedActivities.${activity.id}.timeslots`);
      resetField(`selectedActivities.${activity.id}.timeSensitivity`);
      // oxlint-disable-next-line react/set-state-in-effect
      setShowDescriptionInput(false);
      setShowTimeInput(false);
      setShowDateInput(false);
    }
  }, [activity.id, activityIsSelected, resetField]);

  const isHidden = activity.hidden;
  const descriptionFromTemplate = activity.description;
  const hasDescriptionFromTemplate = Boolean(descriptionFromTemplate);
  const hasDoubleStaffing = activity.doubleStaffing;
  const hasIndividualTimeConfig =
    activity.timeConfigType ===
    batchTemplateActivityTimeConfigTypeSchema.enum.individual;

  const canEditTime = hasIndividualTimeConfig;
  const isRecurring =
    activity.frequency === activityFrequencySchema.enum.recurring;

  const weekdayOffset = activity.delayedStartDateWeekday
    ? t(
        weekdayDictionary[activity.delayedStartDateWeekday].regular,
      ).toLocaleLowerCase()
    : null;
  const weekdayOffsetLabel = weekdayOffset
    ? t`Nästkommande ${weekdayOffset} efter valt startdatum`
    : null;

  const isAnyTimeOfDay = activity.timeCategory === timeOfDaySchema.enum.Any;
  const timeLabel = isAnyTimeOfDay
    ? t(timeOfDayDictionary.Any.short)
    : activity.timeslots && activity.timeslots.length > 0
      ? activity.timeslots.map((slot) => slot.time).join(", ")
      : undefined;

  const recurrenceLabelText = getRecurrenceLabel({
    recurrence: activity.recurrence,
    customRecurrenceInterval: activity.customRecurrenceInterval,
    customRecurrenceIntervalUnit: activity.customRecurrenceIntervalUnit,
    weekdays: activity.weekdays,
  });

  return (
    <div className={styles.activityWrapper}>
      <div className={styles.activityCheckboxWrapper}>
        <Checkbox
          label={{
            text: activity.title,
            component: <ActivityCheckboxDisplayLabel activity={activity} />,
          }}
          {...register(`selectedActivities.${activity.id}.selected`, {
            onChange: () => clearErrors("root.noSelectedActivities"),
          })}
        />
        {isHidden ? <HiddenIcon /> : null}
        {activity.requiredCompetences &&
        activity.requiredCompetences.length > 0 ? (
          <div className={styles.competences}>
            {activity.requiredCompetences.map((competence) => (
              <CompetenceChip
                key={competence}
                competence={competence}
                state="neutral"
                size="small"
              />
            ))}
            {hasDoubleStaffing ? (
              <RequirementChip
                requirement={activityRequirementSchema.enum.DoubleStaffing}
                state="neutral"
                size="small"
              />
            ) : null}
          </div>
        ) : null}
        {activity.measurements && activity.measurements.length > 0 ? (
          <div className={styles.measurements}>
            {activity.measurements.map((measurement) => (
              <MeasurementChip
                key={measurement}
                measurement={measurement}
                size="small"
              />
            ))}
          </div>
        ) : null}
        {activityIsSelected ? (
          <div className={styles.activityEditButtons}>
            {!showDescriptionInput ? (
              <PlainButton
                type="button"
                size="small"
                weight="light"
                onClick={() => setShowDescriptionInput(true)}
              >
                {hasDescriptionFromTemplate ? (
                  <>
                    <PenIcon />
                    <Trans>Ändra beskrivning</Trans>
                  </>
                ) : (
                  <>
                    <PlusIcon />
                    <Trans>Beskrivning</Trans>
                  </>
                )}
              </PlainButton>
            ) : null}
            {canEditTime && !showTimeInput ? (
              <PlainButton
                type="button"
                size="small"
                weight="light"
                onClick={() => {
                  setShowTimeInput(true);
                  setShowDateInput(true);
                }}
              >
                <ClockIcon />
                <Trans>Ändra tid</Trans>
              </PlainButton>
            ) : null}
          </div>
        ) : null}
      </div>
      {hasIndividualTimeConfig ? (
        <div className={styles.startDateOffsetAndSchedule}>
          {showDateInput ? null : (
            <>
              {activity.delayedStartDate ===
              batchTemplateActivityDelayedStartDateOptionsSchema.enum.none ? (
                <Text element="p" size="small">
                  <Trans>Samtidigt som valt startdatum</Trans>
                </Text>
              ) : null}

              {activity.delayedStartDate ===
                batchTemplateActivityDelayedStartDateOptionsSchema.enum
                  .offset &&
              activity.delayedStartDateOffset &&
              activity.delayedStartDateUnit ? (
                <Text element="p" size="small">
                  {activity.delayedStartDateUnit ===
                  batchTemplateActivityDelayedStartDateUnitSchema.enum.week ? (
                    <Plural
                      value={activity.delayedStartDateOffset}
                      _1="1 vecka efter valt startdatum"
                      other="# veckor efter valt startdatum"
                    />
                  ) : (
                    <Plural
                      value={activity.delayedStartDateOffset}
                      _1="1 dag efter valt startdatum"
                      other="# dagar efter valt startdatum"
                    />
                  )}
                </Text>
              ) : null}

              {activity.delayedStartDate ===
                batchTemplateActivityDelayedStartDateOptionsSchema.enum
                  .weekday &&
              activity.delayedStartDateWeekday &&
              weekdayOffsetLabel ? (
                <Text element="p" size="small">
                  {weekdayOffsetLabel}
                </Text>
              ) : null}

              {activity.delayedStartDate ===
              batchTemplateActivityDelayedStartDateOptionsSchema.enum
                .userSelects ? (
                <Text element="p" size="small">
                  {isRecurring ? (
                    <Trans>Välj startdatum</Trans>
                  ) : (
                    <Trans>Välj datum</Trans>
                  )}
                </Text>
              ) : null}
            </>
          )}

          <div className={styles.schedule}>
            {isRecurring && recurrenceLabelText ? (
              <div className={styles.recurrenceLabel}>
                <RecurrenceIcon />
                <Text element="span" size="small" color="faded">
                  {recurrenceLabelText}
                </Text>
              </div>
            ) : null}
            {!showTimeInput ? (
              <Text element="span" size="small" color="faded">
                {timeLabel}
              </Text>
            ) : null}
          </div>
        </div>
      ) : null}
      {hasDescriptionFromTemplate && !showDescriptionInput ? (
        <div className={styles.templateDescription}>
          <Text element="p" color="faded" size="small">
            {descriptionFromTemplate}
          </Text>
        </div>
      ) : null}
      {showDescriptionInput || showTimeInput || showDateInput ? (
        <div className={styles.activityEditFields}>
          <FormFieldPathPrefixContextProvider
            prefix={`selectedActivities.${activity.id}`}
          >
            {showDescriptionInput ? <TemplateDescriptionInput /> : null}
            {showTimeInput ? (
              <TemplateFieldValidationContextProvider
                value={{ isInFinalValidationPhase: true }}
              >
                <TemplateTimeSlotsPicker
                  isRecurring={isRecurring}
                  size="compact"
                />
              </TemplateFieldValidationContextProvider>
            ) : null}
            {showDateInput ? (
              <TemplateDateInput size="compact" hasEndDate={isRecurring} />
            ) : null}
          </FormFieldPathPrefixContextProvider>
        </div>
      ) : null}
    </div>
  );
};

const ActivityCheckboxDisplayLabel = ({
  activity,
}: {
  activity: IBatchTemplateActivity;
}) => {
  return (
    <div className={styles.activityCheckboxLabel}>
      <CategoryIcon size="small" category={activity.category} />
      <Text element="span">{activity.title}</Text>
    </div>
  );
};
